import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Workflow, Sparkles, Plus, Play, Pause, Trash2, Zap, Target, Flame,
  Clock, ArrowRight, History, Wand2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TEMPLATE_ICONS = { target: Target, flame: Flame, sparkles: Sparkles, clock: Clock };

export default function AutomationList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spawning, setSpawning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, tplRes] = await Promise.all([
        api.get("/automations"),
        api.get("/automations/templates"),
      ]);
      setItems(listRes.data.items || []);
      setTemplates(tplRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load automations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const spawnFromTemplate = async (tpl) => {
    setSpawning(tpl.id);
    try {
      const { data } = await api.post("/automations", {
        name: tpl.name, description: tpl.description, enabled: false, canvas: tpl.canvas,
      });
      toast.success(`Created "${data.name}" — customise and enable.`);
      nav(`/app/automation/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create automation");
    } finally { setSpawning(null); }
  };

  const createBlank = async () => {
    try {
      const { data } = await api.post("/automations", {
        name: "Untitled automation",
        description: "",
        enabled: false,
        canvas: {
          nodes: [{
            id: "trigger-1", type: "trigger.lead_status_changed",
            position: { x: 100, y: 200 }, data: { to_status: "qualified", label: "Lead → Qualified" },
          }],
          edges: [],
        },
      });
      toast.success("Blank automation created — start dragging from the palette.");
      nav(`/app/automation/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create automation");
    }
  };

  const toggle = async (auto) => {
    try {
      const { data } = await api.post(`/automations/${auto.id}/toggle`);
      setItems((prev) => prev.map((a) => (a.id === auto.id ? { ...a, enabled: data.enabled } : a)));
      toast.success(data.enabled ? `"${auto.name}" enabled — trigger events will fire it now.` : `"${auto.name}" paused.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (auto) => {
    try {
      await api.delete(`/automations/${auto.id}`);
      setItems((prev) => prev.filter((a) => a.id !== auto.id));
      toast.success("Automation deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="automation-list-page">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground inline-flex items-center gap-2">
            <Workflow className="h-3 w-3" /> Automation · Fire actions on their own
          </div>
          <h1 className="font-display text-4xl mt-1">Set it once. Let Kairosa work.</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Connect triggers → filters → delays → actions on a visual canvas. The moment a lead
            heats up, hits a follow-up date, or submits a form, your automations fire — no nudge required.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/automation/runs" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground" data-testid="auto-runs-link">
            <History className="h-3.5 w-3.5" /> Run history
          </Link>
          <Button onClick={createBlank} className="rounded-full gap-2" data-testid="auto-create-blank">
            <Plus className="h-4 w-4" /> Blank canvas
          </Button>
        </div>
      </div>

      {/* Templates gallery */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3 inline-flex items-center gap-2">
          <Wand2 className="h-3 w-3" /> Starter recipes — click any card to spawn a working automation
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="auto-templates-grid">
          {templates.map((t) => {
            const Icon = TEMPLATE_ICONS[t.icon] || Zap;
            const busy = spawning === t.id;
            return (
              <button
                key={t.id}
                onClick={() => spawnFromTemplate(t)}
                disabled={busy}
                data-testid={`auto-template-${t.id}`}
                className="group relative text-left rounded-xl border border-border bg-card p-5 hover:border-primary/60 hover:-translate-y-0.5 transition disabled:opacity-60"
              >
                <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="mt-3 font-medium">{t.name}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.description}</div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-primary inline-flex items-center gap-1">
                  {busy ? "Spawning…" : "Use this recipe"} <ArrowRight className="h-3 w-3" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Existing automations */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3 inline-flex items-center gap-2">
          <CheckCircle2 className="h-3 w-3" /> Your automations
          {items.length > 0 && <span className="text-foreground/70">· {items.length}</span>}
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
            <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-primary/10 text-primary mb-3">
              <Sparkles className="h-6 w-6" />
            </div>
            <div className="font-medium">No automations yet</div>
            <div className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              Pick a starter recipe above or build from a blank canvas.
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="auto-list-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Runs</th>
                  <th className="text-left px-4 py-3">Last fire</th>
                  <th className="px-4 py-3 w-40" />
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition" data-testid={`auto-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/app/automation/${a.id}`} className="font-medium hover:underline" data-testid={`auto-open-${a.id}`}>
                        {a.name}
                      </Link>
                      {a.description && <div className="text-xs text-muted-foreground line-clamp-1 max-w-md">{a.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={!!a.enabled} onCheckedChange={() => toggle(a)} data-testid={`auto-toggle-${a.id}`} />
                        <span className={"text-xs " + (a.enabled ? "text-primary font-medium" : "text-muted-foreground")}>
                          {a.enabled ? "Live" : "Paused"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">{a.run_count || 0}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {a.last_run_at ? new Date(a.last_run_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/app/automation/${a.id}`}>
                          <Button size="sm" variant="ghost" className="rounded-full text-xs" data-testid={`auto-edit-${a.id}`}>Edit</Button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="h-8 w-8 grid place-items-center rounded hover:bg-destructive/20 text-destructive" aria-label="Delete" data-testid={`auto-delete-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{a.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes the automation and its run history. Cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(a)} data-testid={`auto-delete-confirm-${a.id}`}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
